import React, { Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';
import LegacyLayout from 'src/layouts/LegacyLayout';
import AdminLayout from 'src/layouts/AdminLayout';
import AuthLayout from '@/layouts/AuthLayout';
import UnAuthenticatedLayout from '@/layouts/UnAuthenticatedLayout';
import FlexUseLayout from 'src/layouts/FlexUserLayout';
import { Loader, ProtectedRoute, RequireAuth } from '@/components';
import { Navigate } from 'react-router';
import NonAuthRoute from '@/components/Authenticate/NonAuthRoute';
import { ResourceUIType } from '@/service/Api/types';
import { currentUserVar } from '@/graphql/operations/user';
import { getPeopleMenuPath } from '@/utils/getPeopleMenuPath';

const Analytics = React.lazy(() => import('./Analytics'));
const Connect = React.lazy(() => import('./Connect'));
const Payouts = React.lazy(() => import('./Payouts'));
const Plans = React.lazy(() => import('./Plans'));
const Users = React.lazy(() => import('./Users'));
const Notifications = React.lazy(() => import('./Notifications'));
const SignIn = React.lazy(() => import('./Auth/Signin'));
const Sso = React.lazy(() => import('./Auth/Sso'));
const PasswordReset = React.lazy(() => import('./Auth/PasswordReset'));
const PasswordForgot = React.lazy(() => import('./Auth/PasswordForgot'));
const PasswordResetPending = React.lazy(() => import('./Auth/PasswordResetPending'));
const EmailLogin = React.lazy(() => import('./Auth/EmailLogin'));
const OTP = React.lazy(() => import('./Auth/OTP'));
const Events = React.lazy(() => import('./Events'));
const PlaybookTemplates = React.lazy(() => import('./PlaybookTemplates'));
const Rewards = React.lazy(() => import('./Rewards'));
const ParticipantTransactions = React.lazy(() => import('./ParticipantTransactions'));
const Widgets = React.lazy(() => import('./Widgets'));
const Performance = React.lazy(() => import('./Performance'));
const PerformanceBuilder = React.lazy(() => import('./Performance/Builder'));
const QualityDataUpload = React.lazy(() => import('./Performance/QualityDataUpload'));
const EmployeeProfile = React.lazy(() => import('./EmployeeProfile'));
const Entities = React.lazy(() => import('./Entities'));
const Employees = React.lazy(() => import('./Employees'));
const EmployeeDetails = React.lazy(() => import('@/pages/Employees/EmployeeDetails'));
const AdminDashboard = React.lazy(() => import('./AdminDashboard'));
const Compliance = React.lazy(() => import('./Compliance'));

const FormulaParameterEditor = React.lazy(() => import('./FormulaParameterEditor'));

const ParticipantEarnings = React.lazy(() => import('./StandAlonePages/ParticipantEarnings'));
const ParticipantRewards = React.lazy(() => import('./StandAlonePages/ParticipantRewards'));
const WidgetDataUpload = React.lazy(() => import('./StandAlonePages/WidgetDataUpload'));
const ConnectSlack = React.lazy(() => import('./ConnectSlack/ConnectSlack'));
const NotFound = React.lazy(() => import('./NotFound'));

// Flex user routes
const MyPlan = React.lazy(() => import('../flex-user-app/Plan'));
const MyPay = React.lazy(() => import('../flex-user-app/Pay'));
const MyPerformance = React.lazy(() => import('../flex-user-app/Performance'));
const WagesService = React.lazy(() => import('./StandAlonePages/WagesService'));
const ComplianceAgent = React.lazy(() => import('../flex-user-app/Compliance'));
const PreferencesAgent = React.lazy(() => import('../flex-user-app/Preferences'));
const OnboardingAgent = React.lazy(() => import('../flex-user-app/Onboarding'));
const EmployeeDashboard = React.lazy(() => import('../flex-user-app/Dashboard'));
const EmployeeSchedule = React.lazy(() => import('../flex-user-app/Widgets/Scheduling'));
const EmployeeScheduling = React.lazy(() => import('../flex-user-app/Widgets/Scheduler'));
const PatientScheduling = React.lazy(() => import('@/flex-user-app/Widgets/Scheduler/PatientScheduling'));
const ScheduleRoutePlanner = React.lazy(() => import('@/flex-user-app/Widgets/ScheduleRoutePlanner'));

// V2 Routes
const People = React.lazy(() => import('./People'));
const Shifts = React.lazy(() => import('./Shifts'));
const User = React.lazy(() => import('./People/User'));
const Groups = React.lazy(() => import('./People/Groups'));

export default function RoutesProvide() {
  const currentUser = useReactiveVar(currentUserVar);
  const peopleMenuPath = getPeopleMenuPath(currentUser);

  return (
    <Routes>
      <Route
        path="/"
        element={
          <RequireAuth>
            <Navigate to={'/'} replace={true} />
          </RequireAuth>
        }
      />
      <Route
        path="admin-dashboard"
        element={
          <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.AdminDashboard }}>
            <Suspense fallback={<Loader />}>
              <AdminDashboard />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="intake-queue"
        element={
          <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Schedules }}>
            <Suspense fallback={<Loader />}>
              <AdminDashboard />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route element={<AdminLayout />}>
        <Route
          path="insights"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Insights }}>
              <Suspense fallback={<Loader />}>
                <Analytics />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="performance"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Insights }}>
              <Suspense fallback={<Loader />}>
                <Performance />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="performance/builder"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Insights }}>
              <Suspense fallback={<Loader />}>
                <PerformanceBuilder />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="performance/upload"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Insights }}>
              <Suspense fallback={<Loader />}>
                <QualityDataUpload />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="users"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Users }}>
              <Suspense fallback={<Loader />}>
                <Users />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="users/:userId"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Users }}>
              <Suspense fallback={<Loader />}>
                <ParticipantTransactions />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="employee/:employeeOrUserId"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Employee }}>
              <Suspense fallback={<Loader />}>
                <EmployeeProfile />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="v2/users"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Users }}>
              <Suspense fallback={<Loader />}>
                <People />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="v2/users/:userId"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Users }}>
              <Suspense fallback={<Loader />}>
                <User />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="v2/users/groups"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Users }}>
              <Suspense fallback={<Loader />}>
                <Groups />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="earnings"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Earnings }}>
              <Suspense fallback={<Loader />}>
                <Payouts />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="events"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Schedules }}>
              <Suspense fallback={<Loader />}>
                <Shifts />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="compliance"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Compliance }}>
              <Suspense fallback={<Loader />}>
                <Compliance />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:page"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Schedules }}>
              <Suspense fallback={<Loader />}>
                <Shifts />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="plans"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Plans }}>
              <Suspense fallback={<Loader />}>
                <Plans />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="notifications"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Notifications }}>
              <Suspense fallback={<Loader />}>
                <Notifications />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="connectors"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Connectors }}>
              <Suspense fallback={<Loader />}>
                <Connect />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="widgets"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Connectors }}>
              <Suspense fallback={<Loader />}>
                <Widgets />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="entities"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Schedules }}>
              <Suspense fallback={<Loader />}>
                <Entities />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="employees"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Schedules }}>
              <Suspense fallback={<Loader />}>
                <Employees />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="employees/:employeeId"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Schedules }}>
              <Suspense fallback={<Loader />}>
                <EmployeeDetails />
              </Suspense>
            </ProtectedRoute>
          }
        />
      </Route>
      <Route element={<LegacyLayout />}>
        <Route
          path="v1/transactions"
          element={
            <ProtectedRoute props={{ accessRoles: ['employee'], resourceName: ResourceUIType.Earnings }}>
              <Suspense fallback={<Loader />}>
                <ParticipantTransactions />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="v1/rewards"
          element={
            <ProtectedRoute props={{ accessRoles: ['employee'], resourceName: ResourceUIType.Rewards }}>
              <Suspense fallback={<Loader />}>
                <Rewards />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="v1/plans"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Plans }}>
              <Suspense fallback={<Loader />}>
                <Plans />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="v1/payouts"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Earnings }}>
              <Suspense fallback={<Loader />}>
                <Payouts />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="v1/connect"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Connectors }}>
              <Suspense fallback={<Loader />}>
                <Connect />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="v1/users"
          element={
            peopleMenuPath === '/employees' ? (
              <Navigate to="/employees" replace />
            ) : (
              <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Users }}>
                <Suspense fallback={<Loader />}>
                  <Users />
                </Suspense>
              </ProtectedRoute>
            )
          }
        />
        <Route
          path="v1/users/:userId"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Users }}>
              <Suspense fallback={<Loader />}>
                <ParticipantTransactions />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="v1/analytics"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Insights }}>
              <Suspense fallback={<Loader />}>
                <Analytics />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="v1/notifications"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Notifications }}>
              <Suspense fallback={<Loader />}>
                <Notifications />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="v1/events"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Events }}>
              <Suspense fallback={<Loader />}>
                <Events />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="v1/templates"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Plans }}>
              <Suspense fallback={<Loader />}>
                <PlaybookTemplates />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="v1/widgets"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Connectors }}>
              <Suspense fallback={<Loader />}>
                <Widgets />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="v1/parameter-builder"
          element={
            <ProtectedRoute props={{ accessRoles: ['admin'], resourceName: ResourceUIType.Plans }}>
              <Suspense fallback={<Loader />}>
                <FormulaParameterEditor />
              </Suspense>
            </ProtectedRoute>
          }
        />
      </Route>
      <Route element={<FlexUseLayout />}>
        <Route
          path="employee-onboarding-agent"
          element={
            <ProtectedRoute props={{ accessRoles: ['employee'], resourceName: ResourceUIType.Dashboard }}>
              <Suspense fallback={<Loader />}>
                <OnboardingAgent />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="employee-compliance-agent"
          element={
            <ProtectedRoute props={{ accessRoles: ['employee'], resourceName: ResourceUIType.Compliance }}>
              <Suspense fallback={<Loader />}>
                <ComplianceAgent />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="employee-preferences-agent"
          element={
            <ProtectedRoute props={{ accessRoles: ['employee'], resourceName: ResourceUIType.Availability }}>
              <Suspense fallback={<Loader />}>
                <PreferencesAgent />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="dashboard"
          element={
            <ProtectedRoute
              props={{ accessRoles: ['admin', 'manager', 'employee'], resourceName: ResourceUIType.Dashboard }}
            >
              <Suspense fallback={<Loader />}>
                <EmployeeDashboard />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="my-schedule"
          element={
            <ProtectedRoute
              props={{ accessRoles: ['admin', 'manager', 'employee'], resourceName: ResourceUIType.Schedules }}
            >
              <Suspense fallback={<Loader />}>
                <EmployeeSchedule />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="my-scheduler"
          element={
            <ProtectedRoute
              props={{ accessRoles: ['admin', 'manager', 'employee'], resourceName: ResourceUIType.Schedules }}
            >
              <Suspense fallback={<Loader />}>
                <EmployeeScheduling />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="my-scheduler/:patientEntityId"
          element={
            <ProtectedRoute
              props={{ accessRoles: ['admin', 'manager', 'employee'], resourceName: ResourceUIType.Schedules }}
            >
              <Suspense fallback={<Loader />}>
                <PatientScheduling />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="my-schedule-route-planner"
          element={
            <ProtectedRoute props={{ accessRoles: ['employee'], resourceName: ResourceUIType.Schedules }}>
              <Suspense fallback={<Loader />}>
                <ScheduleRoutePlanner />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="my-plan"
          element={
            <ProtectedRoute props={{ accessRoles: ['employee'], resourceName: ResourceUIType.Plans }}>
              <Suspense fallback={<Loader />}>
                <MyPlan />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="my-pay"
          element={
            <Suspense fallback={<Loader />}>
              <MyPay />
            </Suspense>
          }
        />
        <Route
          path="my-performance"
          element={
            <ProtectedRoute props={{ accessRoles: ['employee'], resourceName: ResourceUIType.Insights }}>
              <Suspense fallback={<Loader />}>
                <MyPerformance />
              </Suspense>
            </ProtectedRoute>
          }
        />
      </Route>
      <Route element={<UnAuthenticatedLayout />}>
        <Route
          path="my-earnings"
          element={
            <Suspense fallback={<Loader />}>
              <ParticipantEarnings />
            </Suspense>
          }
        />
        <Route
          path="my-rewards"
          element={
            <Suspense fallback={<Loader />}>
              <ParticipantRewards />
            </Suspense>
          }
        />
        <Route
          path="my-transactions"
          element={
            <Suspense fallback={<Loader />}>
              <ParticipantTransactions />
            </Suspense>
          }
        />
      </Route>
      <Route element={<AuthLayout />}>
        <Route
          path="auth/sso"
          element={
            <NonAuthRoute>
              <Suspense fallback={<Loader />}>
                <Sso />
              </Suspense>
            </NonAuthRoute>
          }
        />
        <Route
          path="signin"
          element={
            <NonAuthRoute>
              <Suspense fallback={<Loader />}>
                <SignIn />
              </Suspense>
            </NonAuthRoute>
          }
        />
        <Route
          path="password-forgot"
          element={
            <NonAuthRoute>
              <Suspense fallback={<Loader />}>
                <PasswordForgot />
              </Suspense>
            </NonAuthRoute>
          }
        />
        <Route
          path="password-reset"
          element={
            <NonAuthRoute>
              <Suspense fallback={<Loader />}>
                <PasswordReset />
              </Suspense>
            </NonAuthRoute>
          }
        />
        <Route
          path="password-reset-pending"
          element={
            <NonAuthRoute>
              <Suspense fallback={<Loader />}>
                <PasswordResetPending />
              </Suspense>
            </NonAuthRoute>
          }
        />
        <Route
          path="email-login"
          element={
            <Suspense fallback={<Loader />}>
              <EmailLogin />
            </Suspense>
          }
        />
        <Route
          path="otp-auth"
          element={
            <NonAuthRoute>
              <Suspense fallback={<Loader />}>
                <OTP />
              </Suspense>
            </NonAuthRoute>
          }
        />
      </Route>
      <Route
        path="connect-slack"
        element={
          <Suspense fallback={<Loader />}>
            <ConnectSlack />
          </Suspense>
        }
      />
      <Route
        path="widget-data-upload"
        element={
          <Suspense fallback={<Loader />}>
            <WidgetDataUpload />
          </Suspense>
        }
      />
      <Route
        path="wages-service-test"
        element={
          <Suspense fallback={<Loader />}>
            <WagesService />
          </Suspense>
        }
      />
      <Route
        path="*"
        element={
          <Suspense fallback={<Loader />}>
            <NotFound />
          </Suspense>
        }
      />
    </Routes>
  );
}
